/**
 * localStorage draft persistence. Holds only the visitor's own keystrokes on
 * their own device; files are excluded; the draft self-expires after 7 days and
 * is cleared on success. Schema is versioned so drift resets silently.
 */

import type { ValuesMap } from './types';
import type { Enhancer } from './enhance';

const KEY = 'iew_wizard_v1';
const SCHEMA = 2;   // 2: wizard collapsed to 3 steps; six fields removed
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

interface Draft {
  v: number;
  saved_at: number;
  step: number;
  values: ValuesMap;
}

export class Storage {
  private timer: number | null = null;

  constructor(private readonly root: HTMLElement, private readonly enhancer: Enhancer) {}

  /** Debounced save (~300 ms) of the whole form + current step. */
  scheduleSave(step: number): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
    }
    this.timer = window.setTimeout(() => this.save(step), 300);
  }

  save(step: number): void {
    const draft: Draft = { v: SCHEMA, saved_at: Date.now(), step, values: this.collectAll() };
    try {
      window.localStorage.setItem(KEY, JSON.stringify(draft));
    } catch {
      // Quota/security errors are non-fatal — the wizard still works.
    }
  }

  /** Load + validate a draft, or null when absent/expired/corrupt. */
  load(): Draft | null {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch {
      return null;
    }
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as Draft;
      if (parsed.v !== SCHEMA || Date.now() - parsed.saved_at > MAX_AGE_MS) {
        this.clear();
        return null;
      }
      return parsed;
    } catch {
      this.clear();
      return null;
    }
  }

  clear(): void {
    try {
      window.localStorage.removeItem(KEY);
    } catch {
      // ignore
    }
  }

  /**
   * Populate inputs from a draft. Select2 tag fields need their <option>
   * elements created first; the phone is set through ITI; counters refresh via
   * an 'input' dispatch.
   */
  restore(draft: Draft, $: JQueryStatic): void {
    Object.keys(draft.values).forEach((key) => {
      const field = this.root.querySelector<HTMLElement>(`.iew-field[data-field="${key}"]`);
      if (!field) {
        return;
      }
      const value = draft.values[key];
      if (value === undefined) {
        return;
      }

      const phone = field.querySelector<HTMLInputElement>('input.iew-phone');
      if (phone && typeof value === 'string') {
        this.enhancer.setPhone(value);
        return;
      }

      const select = field.querySelector<HTMLSelectElement>('select');
      if (select) {
        const arr = Array.isArray(value) ? value : [value];
        if (select.multiple && select.hasAttribute('data-tags')) {
          arr.forEach((v) => this.enhancer.addTagOption(select, v));
        } else {
          arr.forEach((v) => {
            const opt = Array.from(select.options).find((o) => o.value === v);
            if (opt) {
              opt.selected = true;
            }
          });
        }
        ($(select) as unknown as JQuery).trigger('change');
        return;
      }

      const radios = field.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      if (radios.length > 0 && typeof value === 'string') {
        radios.forEach((r) => {
          r.checked = r.value === value;
        });
        return;
      }

      const repeater = field.querySelector<HTMLElement>('.iew-repeater');
      if (repeater && Array.isArray(value)) {
        this.restoreRepeater(repeater, value);
        return;
      }

      const textarea = field.querySelector<HTMLTextAreaElement>('textarea');
      if (textarea && typeof value === 'string') {
        textarea.value = value;
        textarea.dispatchEvent(new Event('input'));
        return;
      }

      const input = field.querySelector<HTMLInputElement>('input');
      if (input && typeof value === 'string') {
        input.value = value;
      }
    });
  }

  private restoreRepeater(repeater: HTMLElement, values: string[]): void {
    const tpl = repeater.querySelector<HTMLTemplateElement>('.iew-repeater__template');
    const add = repeater.querySelector<HTMLButtonElement>('.iew-repeater__add');
    const rows = () => repeater.querySelectorAll<HTMLElement>('.iew-repeater__row');

    values.forEach((val, idx) => {
      let row = rows()[idx];
      if (!row && tpl && add) {
        const frag = tpl.content.cloneNode(true) as DocumentFragment;
        const cloned = frag.querySelector<HTMLElement>('.iew-repeater__row');
        if (cloned) {
          add.insertAdjacentElement('beforebegin', cloned);
          row = cloned;
        }
      }
      const input = row?.querySelector<HTMLInputElement>('input');
      if (input) {
        input.value = val;
      }
    });
  }

  private collectAll(): ValuesMap {
    const values: ValuesMap = {};
    this.root.querySelectorAll<HTMLElement>('.iew-field[data-field]').forEach((field) => {
      const key = field.dataset.field;
      if (!key) {
        return;
      }
      // Files are intentionally not persisted.
      if (field.querySelector('input[type="file"]')) {
        return;
      }

      // Phone field: intl-tel-input injects its own input into the .iti wrapper,
      // so a generic querySelector('input') can read the wrong (empty) one. Match
      // .iew-phone explicitly and store the E.164 value — mirrors collectStep().
      const phone = field.querySelector<HTMLInputElement>('input.iew-phone');
      if (phone) {
        values[key] = this.enhancer.phoneNumber();
        return;
      }

      const multi = field.querySelector<HTMLSelectElement>('select[multiple]');
      if (multi) {
        values[key] = Array.from(multi.selectedOptions).map((o) => o.value);
        return;
      }
      const single = field.querySelector<HTMLSelectElement>('select:not([multiple])');
      if (single) {
        values[key] = single.value;
        return;
      }
      const radios = field.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      if (radios.length > 0) {
        const checked = Array.from(radios).find((r) => r.checked);
        values[key] = checked ? checked.value : '';
        return;
      }
      const repeater = field.querySelector<HTMLElement>('.iew-repeater');
      if (repeater) {
        const inputs = repeater.querySelectorAll<HTMLInputElement>('.iew-repeater__row input');
        values[key] = Array.from(inputs).map((i) => i.value.trim()).filter((v) => v !== '');
        return;
      }
      const textarea = field.querySelector<HTMLTextAreaElement>('textarea');
      if (textarea) {
        values[key] = textarea.value;
        return;
      }
      const input = field.querySelector<HTMLInputElement>('input');
      if (input) {
        values[key] = input.value;
      }
    });
    return values;
  }
}
